import { compact } from 'lodash';

import { TFunction } from '@lyearn/i18n';
import {
  BookmarkOutline,
  ClockOutline,
  CompassOutline,
  File,
  Home2Outline,
  MessageCircleOutline,
  NucleoAllTask,
  NucleoBarChartOutline,
  NucleoBookOpen,
  NucleoBrain,
  NucleoCalendar,
  NucleoCameraRecordFilmMeetings,
  NucleoChartPie,
  NucleoContent,
  NucleoCrosshairsTarget,
  NucleoDialogue1On1ChatTalk,
  NucleoDottedCircleCheck,
  NucleoFeedbackDialogueThinkSpeech,
  NucleoFolderOpen,
  NucleoHabitsLeaf,
  NucleoListTree,
  NucleoMeterReview,
  NucleoPraiseStar,
  NucleoQuickstart,
  NucleoStack,
  NucleoSurvey,
  NucleoUnorderedList,
  NucleoUser,
  NucleoUsers2,
  TvOutline,
} from '@lyearn/icons';
import { FeatureFlags, FeatureFlagsMap } from '@/helper/FeatureFlags';
import { DEFAULT_FEEDBACK_TAB } from '@/modules/corporate/modules/feedback/views/FeedbackWall/utils';
import { PraisesTabEnum } from '@/modules/praises/views/PraiseWall/types';
import { QuestionManagerTabEnum } from '@/modules/question/views/QuestionManager/types';
import { MyUserSurveysTabType } from '@/modules/user-survey/views/MyUserSurveys/types';
import routes from '@/pages/corporate/routes';

import { NAV_GROUP_IDS, NAV_IDS, NAV_SECTION_IDS } from '../consts';
import { NavItem, NavSection, NavSubGroup, NavSubItem, UnifiedNav } from '../types';

export interface BuildUnifiedNavOptions {
  t: TFunction;
  flags: FeatureFlagsMap;
  communityId?: string;
  defaultWidgetId?: string;
  isQuestionBankDisabled?: boolean;
}

const toGroup = (id: string, items: (NavSubItem | null)[]): NavSubGroup | null => {
  const enabledItems = compact(items);

  return enabledItems.length ? { id, items: enabledItems } : null;
};

const toParent = (item: NavItem, groups: (NavSubGroup | null)[]): NavItem | null => {
  const enabledGroups = compact(groups);

  return enabledGroups.length ? { ...item, groups: enabledGroups } : null;
};

const toSection = (id: string, items: (NavItem | null)[]): NavSection | null => {
  const enabledItems = compact(items);

  return enabledItems.length ? { id, items: enabledItems } : null;
};

export const buildUnifiedNav = ({
  t,
  flags,
  communityId,
  defaultWidgetId,
  isQuestionBankDisabled,
}: BuildUnifiedNavOptions): UnifiedNav => {
  const {
    [FeatureFlags.EnableOnboarding]: isOnboardingEnabled,
    [FeatureFlags.EnabledMGSAOnboarding]: isMgsaOnboardingEnabled,
    [FeatureFlags.EnableProductivityModel]: isProductivityModelEnabled,
    [FeatureFlags.EnablePerformModule]: isPerformModuleEnabled,
    [FeatureFlags.EnableLearnModule]: isLearnModuleEnabled,
    [FeatureFlags.EnableEngageModule]: isEngageModuleEnabled,
    [FeatureFlags.EnableOKR]: isOkrEnabled,
    [FeatureFlags.EnablePerformanceReviews]: isPerformanceReviewsEnabled,
    [FeatureFlags.EnableHabits]: isHabitsEnabled,
    [FeatureFlags.EnableEmployeeJourney]: isEmployeeJourneyEnabled,
    [FeatureFlags.EnableUserSurvey]: isUserSurveyEnabled,
    [FeatureFlags.EnableMeeting]: isMeetingEnabled,
    [FeatureFlags.EnableOneOnOne]: isOneOnOneEnabled,
    [FeatureFlags.EnablePraise]: isPraiseEnabled,
    [FeatureFlags.EnableFeedback]: isFeedbackEnabled,
    [FeatureFlags.EnableReports]: isReportsEnabled,
  } = flags;

  const primary = toSection(NAV_SECTION_IDS.Primary, [
    isOnboardingEnabled || isMgsaOnboardingEnabled
      ? {
          id: NAV_IDS.GetStarted,
          label: t('Admin:GettingStarted'),
          icon: NucleoQuickstart,
          href: routes.GettingStarted(),
        }
      : null,
    {
      id: NAV_IDS.Home,
      label: t('Common:Routes.Home'),
      icon: Home2Outline,
      href: routes.Home(),
    },
    {
      id: NAV_IDS.Actions,
      label: t('Common:Routes.Actions', 'Actions'),
      icon: NucleoDottedCircleCheck,
      isDisabled: true,
    },
    {
      id: NAV_IDS.Agents,
      label: t('Common:Routes.Agents', 'Agents'),
      icon: NucleoBrain,
      isDisabled: true,
    },
  ]);

  const people = toParent(
    {
      id: NAV_IDS.People,
      label: t('Common:Routes.People', 'People'),
      icon: NucleoUsers2,
    },
    [
      toGroup(NAV_GROUP_IDS.PeopleMain, [
        {
          id: NAV_IDS.PeoplePulse,
          label: t('Common:Routes.PeoplePulse', 'People Pulse'),
          icon: NucleoBarChartOutline,
          isDisabled: true,
        },
        {
          id: NAV_IDS.OrgStructure,
          label: t('Common:Routes.OrgStructure', 'Org Structure'),
          icon: NucleoListTree,
          href: routes.WorkeffyOrgStructure(),
          matchPrefix: true,
        },
        {
          id: NAV_IDS.Employees,
          label: t('Admin:Employees'),
          icon: NucleoUser,
          href: routes.Learners(),
          matchPrefix: true,
        },
      ]),
    ],
  );

  const productivity = toParent(
    {
      id: NAV_IDS.Productivity,
      label: t('Common:Routes.Productivity', 'Productivity'),
      icon: NucleoCrosshairsTarget,
    },
    [
      toGroup(NAV_GROUP_IDS.ProductivityOverview, [
        {
          id: NAV_IDS.ProductivityPulse,
          label: t('Common:Routes.ProductivityPulse', 'Productivity Pulse'),
          icon: NucleoBarChartOutline,
          isDisabled: true,
        },
        isProductivityModelEnabled
          ? {
              id: NAV_IDS.Mgsa,
              label: t('Common:Routes.MGSA', 'MGSA'),
              icon: NucleoStack,
              href: routes.ProductivityManager(),
              matchPrefix: true,
            }
          : null,
      ]),
      toGroup(NAV_GROUP_IDS.ProductivityOkrs, [
        isPerformModuleEnabled && isOkrEnabled
          ? {
              id: NAV_IDS.Okrs,
              label: t('Common:Routes.OKRs'),
              icon: NucleoCrosshairsTarget,
              href: routes.WorkEffyAllOKRs(),
            }
          : null,
        isPerformModuleEnabled && isOkrEnabled
          ? {
              id: NAV_IDS.Cycles,
              label: t('Common:Routes.Cycles'),
              icon: NucleoCalendar,
              href: routes.WorkEffyManageCycles(),
            }
          : null,
      ]),
      toGroup(NAV_GROUP_IDS.ProductivityPerform, [
        isPerformModuleEnabled && isPerformanceReviewsEnabled
          ? {
              id: NAV_IDS.Reviews,
              label: t('Common:Routes.Reviews'),
              icon: NucleoMeterReview,
              href: routes.ReviewsListMy(),
              matchPrefix: true,
            }
          : null,
        {
          id: NAV_IDS.Tasks,
          label: t('Common:Routes.Tasks'),
          icon: NucleoAllTask,
          href: routes.Tasks(),
          matchPrefix: true,
        },
        isHabitsEnabled
          ? {
              id: NAV_IDS.Habits,
              label: t('Common:Routes.Habits'),
              icon: NucleoHabitsLeaf,
              href: routes.Habits(),
              matchPrefix: true,
            }
          : null,
      ]),
    ],
  );

  const learning = isLearnModuleEnabled
    ? toParent(
        {
          id: NAV_IDS.Learning,
          label: t('Common:Routes.Learning', 'Learning'),
          icon: NucleoBookOpen,
        },
        [
          toGroup(NAV_GROUP_IDS.LearningOverview, [
            {
              id: NAV_IDS.LearningPulse,
              label: t('Common:Routes.LearningPulse', 'Learning Pulse'),
              icon: NucleoBarChartOutline,
              isDisabled: true,
            },
            {
              id: NAV_IDS.Library,
              label: t('Common:Routes.Library'),
              icon: NucleoBookOpen,
              href: defaultWidgetId ? routes.Discover(defaultWidgetId) : undefined,
              isDisabled: !defaultWidgetId,
            },
            {
              id: NAV_IDS.Bookmark,
              label: t('Common:Routes.Bookmark', 'Bookmark'),
              icon: BookmarkOutline,
              href: routes.Bookmarks(),
            },
          ]),
          toGroup(NAV_GROUP_IDS.LearningContent, [
            {
              id: NAV_IDS.AllContent,
              label: t('Admin:AllContent'),
              icon: NucleoContent,
              href: routes.LearningContent(),
            },
            {
              id: NAV_IDS.MyRecents,
              label: t('Admin:MyRecents'),
              icon: ClockOutline,
              href: routes.RecentLearningContent(),
            },
            {
              id: NAV_IDS.Collections,
              label: t('Admin:Collections'),
              icon: NucleoFolderOpen,
              href: routes.CollectionManager(),
              matchPrefix: true,
            },
            isQuestionBankDisabled
              ? null
              : {
                  id: NAV_IDS.QuestionBank,
                  label: t('Admin:QuestionBank'),
                  icon: NucleoUnorderedList,
                  href: routes.AdminQuestionManager(QuestionManagerTabEnum.Active),
                },
          ]),
          toGroup(NAV_GROUP_IDS.LearningDelivery, [
            {
              id: NAV_IDS.Sessions,
              label: t('Common:Routes.Sessions'),
              icon: TvOutline,
              href: routes.AdminEvents(),
            },
            {
              id: NAV_IDS.Assignment,
              label: t('Common:Routes.Assignment', 'Assignment'),
              icon: File,
              href: routes.Assignments(),
              matchPrefix: true,
            },
            isEmployeeJourneyEnabled
              ? {
                  id: NAV_IDS.Journeys,
                  label: t('Common:Routes.Journeys'),
                  icon: CompassOutline,
                  href: routes.JourneyListing(),
                  matchPrefix: true,
                }
              : null,
          ]),
        ],
      )
    : null;

  const engagement = isEngageModuleEnabled
    ? toParent(
        {
          id: NAV_IDS.Engagement,
          label: t('Common:Routes.Engagement', 'Engagement'),
          icon: NucleoFeedbackDialogueThinkSpeech,
        },
        [
          toGroup(NAV_GROUP_IDS.EngagementMain, [
            isUserSurveyEnabled
              ? {
                  id: NAV_IDS.Surveys,
                  label: t('Common:Routes.Surveys'),
                  icon: NucleoSurvey,
                  href: routes.MySurveys(MyUserSurveysTabType.Surveys),
                }
              : null,
            communityId
              ? {
                  id: NAV_IDS.Community,
                  label: t('Common:Routes.Community'),
                  icon: MessageCircleOutline,
                  href: routes.CommunityDetails(communityId),
                  matchPrefix: true,
                }
              : null,
            isMeetingEnabled
              ? {
                  id: NAV_IDS.Meetings,
                  label: t('Common:Routes.Meetings'),
                  icon: NucleoCameraRecordFilmMeetings,
                  href: routes.teamMeetingsDashboard(),
                  matchPrefix: true,
                }
              : null,
            isOneOnOneEnabled
              ? {
                  id: NAV_IDS.OneOnOne,
                  label: t('Common:Routes.OneOnOne', '1:1'),
                  icon: NucleoDialogue1On1ChatTalk,
                  href: routes.oneOnOnes(),
                  matchPrefix: true,
                }
              : null,
            isPraiseEnabled
              ? {
                  id: NAV_IDS.Praise,
                  label: t('Common:Routes.Praise'),
                  icon: NucleoPraiseStar,
                  href: routes.Praises(PraisesTabEnum.All),
                  matchPrefix: true,
                }
              : null,
            isFeedbackEnabled
              ? {
                  id: NAV_IDS.Feedback,
                  label: t('Common:Routes.Feedback'),
                  icon: NucleoFeedbackDialogueThinkSpeech,
                  href: routes.Feedback(DEFAULT_FEEDBACK_TAB),
                  matchPrefix: true,
                }
              : null,
          ]),
        ],
      )
    : null;

  const modules = toSection(NAV_SECTION_IDS.Modules, [
    people,
    productivity,
    learning,
    engagement,
    isReportsEnabled
      ? {
          id: NAV_IDS.Analytics,
          label: t('Common:Routes.Analytics'),
          icon: NucleoChartPie,
          href: routes.LearnerReports(),
          matchPrefix: true,
        }
      : null,
  ]);

  return compact([primary, modules]);
};
